import { useRef, useState } from 'react';

import { categoryWithSlider, CUSTOM_PLAN_DATA } from '@/widgets/pricing/pricingCards/lib/constants';
import { PlansByCategory } from '@/widgets/pricing/pricingCards/lib/types';

import { useUserBalanceStore } from '@/entities/balance';
import { useCurrentUser, useIsPaidTrial } from '@/entities/user/currentUser';

import { PaymentInterval, Plan } from '@/shared/types/Billing';

import { getPlanPricesToRender } from '../utils/getPlanPricesToRender';

type UsePricingCardStateParams = {
  plansByCategory: PlansByCategory;
  selectedPaymentPeriod: PaymentInterval;
};

export const usePricingCardState = ({ plansByCategory, selectedPaymentPeriod }: UsePricingCardStateParams) => {
  const balance = useUserBalanceStore();
  const { data: user } = useCurrentUser();
  const { plans } = plansByCategory;

  const subscription = user?.subscription;
  const isPaidTrial = useIsPaidTrial(subscription) === true && !balance.hasEnterprisePlan;
  const userPlanId = subscription?.plan?.id;
  const userProductId = subscription?.plan?.product_id;
  const hasInactiveSubscription = balance.hasCanceledSubscription || balance.hasExpiredSubscription;

  // === Active Plan Selection ===
  const userPlanInCategory = hasInactiveSubscription
    ? undefined
    : plans.find((plan) => plan.id === userProductId);
  // Grids C and D list one bundle per category. A second one is the subscriber's own plan outside the grid:
  // the card keeps offering the grid bundle and mentions the current plan separately.
  const currentPlanOutsideGrid = userPlanInCategory && plans.length > 1 ? userPlanInCategory : undefined;
  const gridPlans = currentPlanOutsideGrid ? plans.filter((plan) => plan.id !== currentPlanOutsideGrid.id) : plans;

  const defaultBundleIndex = gridPlans.findIndex((plan) => plan.metadata.bundle_value_default);
  const fallbackIndex = defaultBundleIndex === -1 ? 0 : defaultBundleIndex;

  const defaultActivePlan = (!currentPlanOutsideGrid && userPlanInCategory) || gridPlans[fallbackIndex];
  const [activePlan, setActivePlan] = useState<Plan>(defaultActivePlan);

  // === Price Calculation ===
  const planPrices = getPlanPricesToRender({
    activePricingCardPlanPrices: activePlan.prices,
    hasInactiveSubscription,
    userPlanPriceId: userPlanId,
  });

  const findPriceByInterval = (interval: PaymentInterval) =>
    planPrices.find((price) => price.recurring?.interval === interval);
  const currentPeriodPrice =
    findPriceByInterval(selectedPaymentPeriod) ??
    findPriceByInterval(PaymentInterval.Year) ??
    findPriceByInterval(PaymentInterval.Month);

  // === Plan Status Flags ===
  const isCustomPlan = activePlan.id === CUSTOM_PLAN_DATA.id;
  const isCurrentProductCard = activePlan.id === userProductId;

  const isSelectedCurrentPlanInterval = selectedPaymentPeriod === subscription?.plan.interval;
  const isCurrentPlan = !hasInactiveSubscription && isCurrentProductCard && isSelectedCurrentPlanInterval;

  // === Subscription Period Info ===
  const isYearlyPeriodSelected = selectedPaymentPeriod === PaymentInterval.Year;
  const isCurrentPriceYearly = currentPeriodPrice?.recurring?.interval === PaymentInterval.Year;
  const hasUserYearlySubscription = subscription?.plan.interval === PaymentInterval.Year;

  // === Bundle/Minutes Calculation ===
  const bundles = plans.map((item) => ({
    label: Number(item.metadata.bundle_value),
    value: item.metadata.bundle_position,
  }));

  const maxMinutesInBundles = Math.max(...bundles.map(({ label }) => label));
  const monthlyMinutes = subscription?.credits?.minutes ?? 0;

  const minBundleIndex = 0;

  const hasMaxMinutes = hasUserYearlySubscription && isCurrentPriceYearly
    ? monthlyMinutes >= maxMinutesInBundles
    : monthlyMinutes > maxMinutesInBundles;

  // === Button Visibility Logic ===

  const isSwitchToMonthlyHidden =
    isCurrentProductCard &&
    !balance.hasTrialingEnterprisePlan &&
    !isCustomPlan &&
    !isYearlyPeriodSelected &&
    !isCurrentPriceYearly &&
    balance.hasUnpaidSubscription;

  // Hide upgrade button when user already has max minutes in current product category
  const isUpgradeHidden = balance.hasActiveSubscription && hasMaxMinutes && isCurrentProductCard;

  const isButtonHidden =
    !isPaidTrial &&
    ((!hasInactiveSubscription && !balance.hasSubscriptionCancelAtPeriodEnd && isCurrentPlan) ||
      isUpgradeHidden ||
      isSwitchToMonthlyHidden);

  // === UI State ===
  const isSliderVisible = categoryWithSlider.has(activePlan.category);
  const isDisabled = false;
  const isInitiallyActiveSubscription = useRef(plans.some((plan) => plan.id === userProductId));

  return {
    activePlan,
    bundles,
    currentPeriodPrice,
    currentPlanOutsideGrid,
    isButtonHidden,
    isCurrentPlan,
    isCustomPlan,
    isDisabled,
    isInitiallyActiveSubscription: isInitiallyActiveSubscription.current,
    isPaidTrial,
    isSliderVisible,
    isYearlyPeriodSelected,
    minBundleIndex,
    selectedBundlePosition: activePlan.metadata.bundle_position,
    setActivePlan,
  };
};
